import { useState, useEffect, useMemo } from 'react';
import { Layers, Info, Database, Map, BarChart3, PoundSterling } from 'lucide-react';
import SelectionPanel from '../components/fleetIntelligence/SelectionPanel';
import InteractiveMap from '../components/fleetIntelligence/InteractiveMap';
import TimeSlider from '../components/fleetIntelligence/TimeSlider';
import ProfitabilityPanel from '../components/fleetIntelligence/ProfitabilityPanel';
import CarbonPanel from '../components/fleetIntelligence/CarbonPanel';
import TruthBrowser from '../components/fleetIntelligence/TruthBrowser';
import { VehicleMovementTimeline } from '../components/fleetIntelligence/movementTimeline';
import { Button } from '@/components/ui/button';
import TruthProfitabilityReport from '../components/fleetIntelligence/TruthProfitabilityReport';
import { authenticatedFetch } from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

type ViewMode = 'map' | 'data' | 'timeline' | 'profitability';

const INGEST_SOURCE_BLURBS: Record<string, string> = {
  samsara: 'Samsara (GPS)',
  optifleet: 'OptiFleet (GPS)',
  supatrak: 'Supatrak (GPS)',
  qargo: 'Qargo (orders)',
  palletways: 'Palletways (orders)',
  aquarius: 'Aquarius (driver hours)',
  jigsaw: 'Jigsaw (fuel)',
  eflex: 'Eflex (fuel)',
};

function formatTenantDataSourcesLine(enabledSources: string[]): string {
  const parts = enabledSources
    .map((id) => INGEST_SOURCE_BLURBS[id] ?? id)
    .filter(Boolean);
  parts.push('User-declared movements');
  return parts.join(', ');
}

export interface SelectedItem {
  id: string;
  type: 'vehicle' | 'order';
  label: string;
  color: string;
  source: string;
}

export interface TimeRange {
  from: string;
  to: string;
}

export default function FleetIntelligence() {
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [enabledIngestSources, setEnabledIngestSources] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    // Default to yesterday
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const from = new Date(yesterday);
    from.setHours(0, 0, 0, 0);
    
    const to = new Date(yesterday);
    to.setHours(23, 59, 59, 999);
    
    return {
      from: from.toISOString(),
      to: to.toISOString(),
    };
  });
  const [currentTimestamp, setCurrentTimestamp] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const dataSourcesLine = useMemo(
    () => formatTenantDataSourcesLine(enabledIngestSources),
    [enabledIngestSources],
  );

  // Tenant-scoped ingest sources for the info banner
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.status());
        if (!res.ok) return;
        const json = (await res.json().catch(() => ({}))) as { enabledSources?: string[] };
        if (!cancelled && Array.isArray(json.enabledSources)) {
          setEnabledIngestSources(json.enabledSources);
        }
      } catch (err) {
        console.warn('[FleetIntelligence] Failed to load enabled ingest sources:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load selections from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('fleetIntelligence.selections');
      if (saved) {
        setSelectedItems(JSON.parse(saved));
      }
    } catch (err) {
      console.error('Failed to load selections from localStorage:', err);
    }
  }, []);

  // Switch to Data Browser when navigated from a vehicle link
  useEffect(() => {
    const handler = () => {
      setViewMode('data');
    };
    window.addEventListener('navigate-to-truth-browser', handler as EventListener);
    return () => window.removeEventListener('navigate-to-truth-browser', handler as EventListener);
  }, []);

  // Save selections to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('fleetIntelligence.selections', JSON.stringify(selectedItems));
    } catch (err) {
      console.error('Failed to save selections to localStorage:', err);
    }
  }, [selectedItems]);

  const handleSelectionsChange = (items: SelectedItem[]) => {
    setSelectedItems(items);
  };

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    // Reset playback when time range changes
    setCurrentTimestamp(null);
    setIsPlaying(false);
  };

  const handlePlaybackControlsChange = (
    playing: boolean,
    speed: number,
    timestamp: string | null
  ) => {
    setIsPlaying(playing);
    setPlaybackSpeed(speed);
    setCurrentTimestamp(timestamp);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
            <Layers className="w-7 h-7 text-success" />
            Fleet Intelligence
          </h2>
          <p className="text-muted-foreground text-sm">
            Multi-source vehicle and order visualization with integrated tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-secondary rounded-lg p-1">
            <Button
              type="button"
              variant={viewMode === 'map' ? 'default' : 'ghost'}
              size="md"
              onClick={() => setViewMode('map')}
              className={viewMode === 'map' ? 'shadow-lg' : undefined}
            >
              <Map className="w-4 h-4" aria-hidden />
              Map View
            </Button>
            <Button
              type="button"
              variant={viewMode === 'data' ? 'default' : 'ghost'}
              size="md"
              onClick={() => setViewMode('data')}
              className={viewMode === 'data' ? 'shadow-lg' : undefined}
            >
              <Database className="w-4 h-4" aria-hidden />
              Data Browser
            </Button>
            <Button
              type="button"
              variant={viewMode === 'timeline' ? 'default' : 'ghost'}
              size="md"
              onClick={() => setViewMode('timeline')}
              className={viewMode === 'timeline' ? 'shadow-lg' : undefined}
            >
              <BarChart3 className="w-4 h-4" aria-hidden />
              Movement Timeline
            </Button>
            <Button
              type="button"
              variant={viewMode === 'profitability' ? 'default' : 'ghost'}
              size="md"
              onClick={() => setViewMode('profitability')}
              className={viewMode === 'profitability' ? 'shadow-lg' : undefined}
            >
              <PoundSterling className="w-4 h-4" aria-hidden />
              Profitability Report
            </Button>
          </div>
          

        </div>
      </div>

      {/* Info Banner - Only show in map view */}
      {viewMode === 'map' && (
        <div className="bg-ring/20 border border-ring/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-ring mt-0.5 flex-shrink-0" />
            <div className="text-sm text-ring">
              <p className="font-medium mb-1">How to use Fleet Intelligence:</p>
              <ul className="list-disc list-inside space-y-1 text-ring/80">
                <li>Select up to 10 vehicles and 10 orders from the selection panel</li>
                <li>Each selection is automatically assigned a unique color</li>
                <li>Use the time slider to filter data and play back historical movements</li>
                <li>Hover over orders to highlight associated vehicle tracks</li>
                <li>View profitability and carbon emissions for selected vehicles</li>
                <li>Data sources: {dataSourcesLine}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Data Browser View */}
      {viewMode === 'data' && (
        <TruthBrowser />
      )}

      {/* Movement Timeline Explorer */}
      {viewMode === 'timeline' && (
        <VehicleMovementTimeline />
      )}

      {/* Profitability Report (Truth Layer) */}
      {viewMode === 'profitability' && (
        <TruthProfitabilityReport />
      )}

      {/* Map View */}
      {viewMode === 'map' && (
        <div className="grid grid-cols-12 gap-6">
          {/* Selection Panel - Left side */}
          <div className="col-span-12 lg:col-span-3">
            <SelectionPanel
              selectedItems={selectedItems}
              onSelectionsChange={handleSelectionsChange}
              timeRange={timeRange}
            />
          </div>

          {/* Map and Time Controls - Right side */}
          <div className="col-span-12 lg:col-span-9 space-y-4">
            {/* Time Slider */}
            <TimeSlider
              timeRange={timeRange}
              onTimeRangeChange={handleTimeRangeChange}
              currentTimestamp={currentTimestamp}
              isPlaying={isPlaying}
              playbackSpeed={playbackSpeed}
              onPlaybackControlsChange={handlePlaybackControlsChange}
            />

            {/* Interactive Map */}
            <div className="bg-card rounded-lg shadow-sm border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
              {selectedItems.length === 0 ? (
                /* Empty State */
                <div className="h-150 flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <Layers className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium mb-2">No items selected</p>
                    <p className="text-sm">
                      Select vehicles and orders from the panel to visualize movements
                    </p>
                  </div>
                </div>
              ) : (
                /* Interactive Map */
                <InteractiveMap
                  selectedItems={selectedItems}
                  timeRange={timeRange}
                  currentTimestamp={currentTimestamp}
                  isPlaying={isPlaying}
                  playbackSpeed={playbackSpeed}
                />
              )}
            </div>

            {/* Profitability and Carbon Panels - Only show when vehicles are selected */}
            {selectedItems.some(item => item.type === 'vehicle') && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Profitability Panel */}
                <ProfitabilityPanel
                  selectedVehicles={selectedItems
                    .filter(item => item.type === 'vehicle')
                    .map(item => item.id)
                  }
                  timeRange={timeRange}
                />

                {/* Carbon Emissions Panel */}
                <CarbonPanel
                  selectedVehicles={selectedItems
                    .filter(item => item.type === 'vehicle')
                    .map(item => item.id)
                  }
                  timeRange={timeRange}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
